import { motion } from 'framer-motion';
import { CatMeme } from '@/mocks/catMemes';
import MemeShareButtons from './MemeShareButtons';

interface FeaturedMemeProps {
  meme: CatMeme;
}

export default function FeaturedMeme({ meme }: FeaturedMemeProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl overflow-hidden border-2 border-amber-200 shadow-xl mb-10"
    >
      <div className="flex flex-col lg:flex-row">
        {/* Image */}
        <div className="relative lg:w-1/2 overflow-hidden">
          <img
            src={meme.image}
            alt={meme.title}
            className="w-full h-72 lg:h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 flex gap-2">
            <span className="bg-amber-400 text-white text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow">
              <i className="ri-star-fill"></i> Meme of the Week
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="lg:w-1/2 p-8 flex flex-col justify-center gap-5">
          <div>
            <span className="text-xs font-bold text-orange-500 uppercase tracking-widest">{meme.category}</span>
            <h2 className="text-3xl font-black text-gray-800 mt-1 mb-3">{meme.title}</h2>
            <p className="text-gray-600 text-lg leading-relaxed italic">"{meme.caption}"</p>
          </div>

          <div className="flex items-center gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5 font-semibold text-rose-500">
              <i className="ri-heart-fill text-base"></i>
              {(meme.likes / 1000).toFixed(1)}k likes
            </span>
            <span className="flex items-center gap-1.5 font-semibold text-teal-500">
              <i className="ri-share-forward-fill text-base"></i>
              {(meme.shares / 1000).toFixed(1)}k shares
            </span>
            <span className="flex items-center gap-1.5 font-semibold text-amber-500">
              <i className="ri-calendar-line text-base"></i>
              {meme.week}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {meme.tags.map((tag) => (
              <span key={tag} className="text-xs text-teal-600 bg-teal-50 border border-teal-100 px-2.5 py-1 rounded-full font-medium">
                #{tag}
              </span>
            ))}
          </div>

          <div className="pt-2 border-t border-amber-200">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Share this meme</p>
            <MemeShareButtons title={meme.title} memeId={meme.id} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
